import React, { useState, useEffect, useRef, useMemo } from "react";
import { formatSwissCurrency } from "../../services/costs/controlCalculations.js";
import { downloadZahlungsanweisungPdf } from "../../services/costs/zahlungsanweisungPdf.js";
import {
  buildContractGroups,
  resolveGroup,
  groupShortLabel,
  groupLabel,
  groupUnternehmer,
} from "../../services/costs/contractGroups.js";
import UnternehmerPicker from "./UnternehmerPicker.jsx";

// Detail-Panel einer BKP-Position: Liste der Verträge/Nachträge (KOST-009)
// und Zahlungen inkl. Akonto (KOST-010). Wird vom CostControlEditor pro
// geöffneter Position gerendert. Formularzustand ist lokal; die eigentliche
// Persistenz erfolgt über die übergebenen (asynchronen) Handler.
// US-KOST-022: Verträge tragen einen Unternehmer (CRM-Firma) und eine
// Vertragsnummer; "+ Zahlungsanweisung" öffnet das PDF-Modal des Editors.
// US-KOST-023: Nachträge gehören zu einem Vertrag (vertragId), Zahlungen
// ebenso — bei mehreren Verträgen der Position ist die Wahl Pflicht
// (Vertragsgruppen, services/costs/contractGroups.js). Der Unternehmer ist
// damit eine Eigenschaft je VERTRAG, nicht je Position: "+ Unternehmen"
// ordnet eine Firma einem Vertrag samt seinen Nachträgen zu (bei mehreren
// Verträgen mit Vertragswahl), die Kopfzeile nennt die Firma je Vertrag.
// "Bearbeiten" einer Zahlung aus einer Zahlungsanweisung öffnet das ZA-Modal
// (inkl. neuem PDF).

const emptyContractForm = {
  bezeichnung: "",
  betrag: "",
  datum: "",
  istNachtrag: "nein",
  vertragNr: "",
  unternehmer: null, // {id, name}
  unternehmerAuto: false, // true = Unternehmer automatisch vorbelegt (folgt dann dem gewählten Vertrag)
  vertragId: "", // Nachtrag: Vertrag, zu dem er gehört
};
const emptyPaymentForm = { bezeichnung: "", betrag: "", datum: "", istAkonto: "", vertragId: "" };

const CostControlDetailPanel = ({
  code,
  nodeName,
  contracts = [],
  payments = [],
  readOnly = false,
  openWith = null, // 'contract' | 'payment' | 'unternehmer' | null -> initial geöffnetes Formular (KOST-012)
  onSaveContract,
  onUpdateContract,
  onDeleteContract,
  onSavePayment,
  onUpdatePayment,
  onDeletePayment,
  // US-KOST-022
  onZahlungsanweisung, // (code) => void — öffnet das Modal im Editor
  partners = [], // CRM-Firmen (projectPartners)
  onPartnersReload,
  projectCode = "",
}) => {
  const [contractForm, setContractForm] = useState(emptyContractForm);
  const [editingContractId, setEditingContractId] = useState(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [showUnternehmerForm, setShowUnternehmerForm] = useState(false);
  // "+ Unternehmen" bei mehreren Verträgen: gewählter Ziel-Vertrag (vertragId)
  const [unternehmerTarget, setUnternehmerTarget] = useState("");

  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const contractInputRef = useRef(null);
  const paymentInputRef = useRef(null);

  // Unternehmer der Position = die auf den Verträgen hinterlegten Firmen (distinct).
  const positionUnternehmer = useMemo(() => {
    const seen = new Map();
    for (const c of contracts) {
      if (c.unternehmerId && !seen.has(c.unternehmerId)) {
        seen.set(c.unternehmerId, { id: c.unternehmerId, name: c.unternehmerName || "" });
      }
    }
    return Array.from(seen.values());
  }, [contracts]);

  // Vertragsgruppen (Vertrag + Nachträge) der Position (US-KOST-023).
  const { groups, unassigned: unassignedNachtraege } = useMemo(() => buildContractGroups(contracts), [contracts]);
  // Im Vertragsformular ist der gerade bearbeitete Vertrag als Ziel seines
  // eigenen Nachtrags ausgeschlossen.
  const groupsForContractForm = groups.filter((g) => g.id !== editingContractId);
  const needsVertragChoice = groups.length > 1;
  // Zuordnung: explizit gewählt, sonst bei genau einem Vertrag automatisch dieser.
  const resolveVertragId = (chosen, options) => chosen || (options.length === 1 ? options[0].id : "");

  // Vorbelegung des Unternehmers für einen NEUEN Vertrag: nur wenn die Position
  // eindeutig eine Firma hat. Bei mehreren Firmen wäre die erste ein Zufall.
  const defaultUnternehmer = positionUnternehmer.length === 1 ? positionUnternehmer[0] : null;
  const newContractForm = () => ({
    ...emptyContractForm,
    unternehmer: defaultUnternehmer,
    unternehmerAuto: !!defaultUnternehmer,
  });

  // "+ Unternehmen": Ziel ist ein Vertrag samt Nachträgen. Bei genau einem
  // Vertrag steht er fest, bei mehreren muss er gewählt werden; ohne Vertrag
  // (nur Nachträge/Altdaten) gelten alle Verträge der Position.
  const unternehmerTargetGroup =
    groups.length > 1 ? groups.find((g) => g.id === unternehmerTarget) || null : groups[0] || null;
  const unternehmerTargets =
    groups.length > 1 ? unternehmerTargetGroup?.contracts || [] : groups.length === 1 ? groups[0].contracts : contracts;
  const openUnternehmerForm = () => {
    setUnternehmerTarget(groups.length === 1 ? groups[0].id : "");
    setShowUnternehmerForm(true);
    setError("");
  };

  // Beim Oeffnen via Kontextmenü (KOST-012) direkt das passende Formular zeigen.
  useEffect(() => {
    if (openWith === "contract") {
      setShowContractForm(true);
      setEditingContractId(null);
      setContractForm(newContractForm());
    } else if (openWith === "payment") {
      setShowPaymentForm(true);
      setEditingPaymentId(null);
      setPaymentForm(emptyPaymentForm);
    } else if (openWith === "unternehmer") {
      openUnternehmerForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWith]);

  useEffect(() => {
    if (showContractForm && contractInputRef.current) contractInputRef.current.focus();
  }, [showContractForm]);
  useEffect(() => {
    if (showPaymentForm && paymentInputRef.current) paymentInputRef.current.focus();
  }, [showPaymentForm]);

  const contractsSum = contracts.reduce((s, c) => s + (Number(c.betrag) || 0), 0);
  const paymentsSum = payments.reduce((s, p) => s + (Number(p.betrag) || 0), 0);

  // === Verträge ===
  const startEditContract = (c) => {
    setEditingContractId(c.id);
    setContractForm({
      bezeichnung: c.bezeichnung,
      betrag: String(c.betrag),
      datum: c.datum || "",
      istNachtrag: c.istNachtrag ? "ja" : "nein",
      vertragNr: c.vertragNr || "",
      unternehmer: c.unternehmerId ? { id: c.unternehmerId, name: c.unternehmerName || "" } : null,
      vertragId: c.vertragId || "",
    });
    setShowContractForm(true);
    setError("");
  };

  const cancelContract = () => {
    setShowContractForm(false);
    setEditingContractId(null);
    setContractForm(emptyContractForm);
    setError("");
  };

  const submitContract = async () => {
    if (!contractForm.bezeichnung.trim()) {
      setError("Bezeichnung des Vertrags ist erforderlich.");
      return;
    }
    const istNachtrag = contractForm.istNachtrag === "ja";
    const nachtragVertragId = istNachtrag ? resolveVertragId(contractForm.vertragId, groupsForContractForm) : "";
    if (istNachtrag && groupsForContractForm.length > 1 && !nachtragVertragId) {
      setError("Bitte den Vertrag wählen, zu dem der Nachtrag gehört.");
      return;
    }
    const payload = {
      bezeichnung: contractForm.bezeichnung.trim(),
      betrag: parseFloat(contractForm.betrag) || 0,
      datum: contractForm.datum || "",
      istNachtrag,
      vertragNr: contractForm.vertragNr.trim(),
      unternehmerId: contractForm.unternehmer?.id || "",
      unternehmerName: contractForm.unternehmer?.name || "",
      vertragId: nachtragVertragId,
    };
    setBusy(true);
    setError("");
    try {
      if (editingContractId) {
        await onUpdateContract(editingContractId, payload);
      } else {
        await onSaveContract(code, payload);
      }
      cancelContract();
    } catch (err) {
      setError(`Vertrag konnte nicht gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  // "+ Unternehmen" (US-KOST-022/023): Firma aus dem CRM waehlen (oder dort neu
  // erfassen) und dem Ziel-Vertrag samt Nachtraegen zuordnen. Ohne Vertraege
  // wird stattdessen das Vertragsformular mit vorbelegtem Unternehmer geoeffnet
  // — der Unternehmer lebt auf den Vertraegen, nicht auf der Position.
  const assignUnternehmer = async (partner) => {
    if (!partner) return;
    const value = { id: partner.id, name: partner.name };
    if (contracts.length === 0) {
      setShowUnternehmerForm(false);
      setEditingContractId(null);
      setContractForm({ ...emptyContractForm, unternehmer: value });
      setShowContractForm(true);
      return;
    }
    if (groups.length > 1 && !unternehmerTargetGroup) {
      setError("Bitte zuerst den Vertrag wählen, dem das Unternehmen zugeordnet wird.");
      return;
    }
    setShowUnternehmerForm(false);
    setBusy(true);
    setError("");
    try {
      for (const c of unternehmerTargets) {
        if (c.unternehmerId === value.id) continue;
        await onUpdateContract(c.id, { ...c, unternehmerId: value.id, unternehmerName: value.name });
      }
    } catch (err) {
      setError(`Unternehmer konnte nicht zugeordnet werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  const removeContract = async (c) => {
    if (!window.confirm(`Vertrag "${c.bezeichnung}" löschen?`)) return;
    setBusy(true);
    setError("");
    try {
      await onDeleteContract(c.id);
    } catch (err) {
      setError(`Vertrag konnte nicht gelöscht werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  // === Zahlungen ===
  const startEditPayment = (p) => {
    setEditingPaymentId(p.id);
    setPaymentForm({
      bezeichnung: p.bezeichnung,
      betrag: String(p.betrag),
      datum: p.datum || "",
      istAkonto: p.istAkonto ? "ja" : "nein",
      vertragId: p.vertragId || resolveGroup(p, groups)?.id || "",
    });
    setShowPaymentForm(true);
    setError("");
  };

  const cancelPayment = () => {
    setShowPaymentForm(false);
    setEditingPaymentId(null);
    setPaymentForm(emptyPaymentForm);
    setError("");
  };

  const submitPayment = async () => {
    if (!paymentForm.bezeichnung.trim()) {
      setError("Bezeichnung der Zahlung ist erforderlich.");
      return;
    }
    if (!paymentForm.datum) {
      setError("Datum der Zahlung ist erforderlich.");
      return;
    }
    if (paymentForm.istAkonto !== "ja" && paymentForm.istAkonto !== "nein") {
      setError("Bitte Akonto ja/nein auswählen.");
      return;
    }
    const vertragId = resolveVertragId(paymentForm.vertragId, groups);
    if (needsVertragChoice && !vertragId) {
      setError("Bitte den Vertrag wählen, zu dem die Zahlung gehört.");
      return;
    }
    const payload = {
      bezeichnung: paymentForm.bezeichnung.trim(),
      betrag: parseFloat(paymentForm.betrag) || 0,
      datum: paymentForm.datum,
      istAkonto: paymentForm.istAkonto === "ja",
      vertragId,
    };
    setBusy(true);
    setError("");
    try {
      if (editingPaymentId) {
        // Zahlungsanweisungs-Felder (ZaNr, Unternehmer, Snapshot) der bestehenden
        // Zahlung mitnehmen — PUT ersetzt die Entity vollstaendig.
        const existing = payments.find((p) => p.id === editingPaymentId) || {};
        await onUpdatePayment(editingPaymentId, { ...existing, ...payload });
      } else {
        await onSavePayment(code, payload);
      }
      cancelPayment();
    } catch (err) {
      setError(`Zahlung konnte nicht gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (p) => {
    const hint = p.zaNr ? ` (Zahlungsanweisung Nr. ${p.zaNr})` : "";
    if (!window.confirm(`Zahlung "${p.bezeichnung}"${hint} löschen?`)) return;
    setBusy(true);
    setError("");
    try {
      await onDeletePayment(p.id);
    } catch (err) {
      setError(`Zahlung konnte nicht gelöscht werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (p) => {
    setError("");
    try {
      await downloadZahlungsanweisungPdf(p.zaDaten);
    } catch (err) {
      setError(`PDF konnte nicht erzeugt werden: ${err?.message || "Unbekannter Fehler"}`);
    }
  };

  // Art-Spalte: Nachträge nennen den Vertrag, zu dem sie gehören (US-KOST-023).
  const renderContractArt = (c) => {
    if (!c.istNachtrag) return "Vertrag";
    const g = resolveGroup(c, groups);
    if (g) return `Nachtrag zu ${groupShortLabel(g)}`;
    return (
      <span className="control-detail-warn" title="Kein Vertrag zugeordnet – über „Bearbeiten“ zuordnen">
        Nachtrag (ohne Vertrag)
      </span>
    );
  };

  const renderPaymentVertrag = (p) => {
    const g = resolveGroup(p, groups);
    if (g) return <span title={groupLabel(g, { withSum: false })}>{groupShortLabel(g)}</span>;
    return (
      <span className="control-detail-warn" title="Kein Vertrag zugeordnet – über „Bearbeiten“ zuordnen">
        —
      </span>
    );
  };

  return (
    <div className="control-detail-panel">
      <div className="control-detail-title">
        {code} · {nodeName || ""}
      </div>

      {error && <div className="control-detail-error">{error}</div>}

      <div className="control-detail-columns">
        {/* Verträge / Nachträge */}
        <div className="control-detail-section">
          <div className="control-detail-section-head">
            <span>
              Verträge / Nachträge
              {positionUnternehmer.length > 0 && (
                <span className="control-detail-unternehmer" title="Unternehmer je Vertrag (aus den Verträgen)">
                  {" · "}
                  {groups.length > 1
                    ? groups
                        .map((g) => `${groupShortLabel(g)}: ${groupUnternehmer(g)?.name || "ohne Unternehmer"}`)
                        .join(" · ")
                    : positionUnternehmer.map((u) => u.name).join(", ")}
                </span>
              )}
            </span>
            <span className="control-detail-sum">Summe: CHF {formatSwissCurrency(contractsSum)}</span>
          </div>

          {contracts.length === 0 && !showContractForm && (
            <div className="control-detail-empty">Keine Verträge erfasst.</div>
          )}

          {contracts.length > 0 && (
            <table className="control-detail-list">
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>Art</th>
                  <th>Nr.</th>
                  <th>Unternehmer</th>
                  <th>Datum</th>
                  <th className="num">Betrag</th>
                  {!readOnly && <th></th>}
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id}>
                    <td>{c.bezeichnung}</td>
                    <td>{renderContractArt(c)}</td>
                    <td>{c.vertragNr || "—"}</td>
                    <td>{c.unternehmerName || "—"}</td>
                    <td>{c.datum ? new Date(c.datum).toLocaleDateString("de-CH") : "—"}</td>
                    <td className="num">CHF {formatSwissCurrency(c.betrag)}</td>
                    {!readOnly && (
                      <td className="control-detail-actions">
                        <button type="button" className="link-btn" onClick={() => startEditContract(c)} disabled={busy}>
                          Bearbeiten
                        </button>
                        <button type="button" className="link-btn danger" onClick={() => removeContract(c)} disabled={busy}>
                          Löschen
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {needsVertragChoice && unassignedNachtraege.length > 0 && (
            <div className="control-detail-hint control-detail-warn">
              {unassignedNachtraege.length} Nachtrag/Nachträge ohne Vertragszuordnung – sie zählen in keiner
              Vertragssumme einer Zahlungsanweisung. Über „Bearbeiten“ dem Vertrag zuordnen.
            </div>
          )}

          {!readOnly && showContractForm && (
            <div className="control-detail-form">
              <input
                ref={contractInputRef}
                type="text"
                placeholder="Bezeichnung *"
                value={contractForm.bezeichnung}
                onChange={(e) => setContractForm({ ...contractForm, bezeichnung: e.target.value })}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Betrag (CHF, inkl. MwSt)"
                value={contractForm.betrag}
                onChange={(e) => setContractForm({ ...contractForm, betrag: e.target.value })}
              />
              <input
                type="date"
                value={contractForm.datum}
                onChange={(e) => setContractForm({ ...contractForm, datum: e.target.value })}
              />
              <select
                value={contractForm.istNachtrag}
                onChange={(e) => setContractForm({ ...contractForm, istNachtrag: e.target.value })}
                title="Vertrag oder Nachtrag"
              >
                <option value="nein">Vertrag</option>
                <option value="ja">Nachtrag</option>
              </select>
              {contractForm.istNachtrag === "ja" && groupsForContractForm.length > 0 && (
                <select
                  className="control-detail-vertrag"
                  value={contractForm.vertragId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const u = groupUnternehmer(groupsForContractForm.find((x) => x.id === id) || null);
                    // Unternehmer folgt dem Vertrag, solange keiner von Hand gewählt wurde.
                    setContractForm((f) => {
                      const folgt = !f.unternehmer || f.unternehmerAuto;
                      return {
                        ...f,
                        vertragId: id,
                        unternehmer: folgt ? u : f.unternehmer,
                        unternehmerAuto: folgt ? !!u : false,
                      };
                    });
                  }}
                  title="Vertrag, zu dem der Nachtrag gehört"
                >
                  <option value="">{groupsForContractForm.length > 1 ? "zu Vertrag… *" : "zu Vertrag…"}</option>
                  {groupsForContractForm.map((g) => (
                    <option key={g.id} value={g.id}>
                      zu {groupLabel(g, { withSum: false })}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                className="control-detail-vertragnr"
                placeholder="Vertrag Nr."
                title="Vertragsnummer (erscheint auf der Zahlungsanweisung)"
                value={contractForm.vertragNr}
                onChange={(e) => setContractForm({ ...contractForm, vertragNr: e.target.value })}
              />
              <div className="control-detail-form-unternehmer">
                <UnternehmerPicker
                  value={contractForm.unternehmer}
                  onChange={(p) =>
                    setContractForm({
                      ...contractForm,
                      unternehmer: p ? { id: p.id, name: p.name } : null,
                      unternehmerAuto: false,
                    })
                  }
                  partners={partners}
                  onPartnersReload={onPartnersReload}
                  projectCode={projectCode}
                  placeholder="Unternehmer (CRM) suchen oder neu erfassen…"
                />
              </div>
              <div className="control-detail-form-actions">
                <button type="button" className="btn btn-secondary btn-small" onClick={cancelContract} disabled={busy}>
                  Abbrechen
                </button>
                <button type="button" className="btn btn-primary btn-small" onClick={submitContract} disabled={busy}>
                  {editingContractId ? "Speichern" : "Hinzufügen"}
                </button>
              </div>
            </div>
          )}

          {!readOnly && showUnternehmerForm && !showContractForm && (
            <div className="control-detail-form control-detail-unternehmer-form">
              <span className="control-detail-form-label">
                {contracts.length === 0
                  ? "Unternehmer für den neuen Vertrag wählen:"
                  : groups.length > 1
                    ? "Unternehmer einem Vertrag (samt Nachträgen) zuordnen:"
                    : "Unternehmer dem Vertrag dieser Position (samt Nachträgen) zuordnen:"}
              </span>
              {groups.length > 1 && (
                <select
                  className="control-detail-vertrag"
                  value={unternehmerTarget}
                  onChange={(e) => setUnternehmerTarget(e.target.value)}
                  title="Vertrag, dem das Unternehmen zugeordnet wird"
                  autoFocus
                >
                  <option value="">Vertrag wählen… *</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {groupLabel(g, { withSum: false })}
                    </option>
                  ))}
                </select>
              )}
              <div className="control-detail-form-unternehmer">
                <UnternehmerPicker
                  value={null}
                  onChange={assignUnternehmer}
                  partners={partners}
                  onPartnersReload={onPartnersReload}
                  projectCode={projectCode}
                  disabled={groups.length > 1 && !unternehmerTargetGroup}
                  placeholder={
                    groups.length > 1 && !unternehmerTargetGroup
                      ? "Zuerst den Vertrag wählen…"
                      : "Unternehmer (CRM) suchen oder neu erfassen…"
                  }
                  autoFocus={groups.length <= 1}
                />
              </div>
              <div className="control-detail-form-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setShowUnternehmerForm(false)}
                  disabled={busy}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {!readOnly && !showContractForm && !showUnternehmerForm && (
            <div className="control-detail-add-row">
              <button
                type="button"
                className="control-detail-add"
                onClick={() => {
                  setEditingContractId(null);
                  setContractForm(newContractForm());
                  setShowContractForm(true);
                }}
              >
                + Vertrag / Nachtrag
              </button>
              <button
                type="button"
                className="control-detail-add"
                onClick={openUnternehmerForm}
                title="Unternehmer aus dem CRM wählen oder dort neu erfassen und einem Vertrag zuordnen"
              >
                + Unternehmen
              </button>
            </div>
          )}
        </div>

        {/* Zahlungen */}
        <div className="control-detail-section">
          <div className="control-detail-section-head">
            <span>Zahlungen</span>
            <span className="control-detail-sum">Summe: CHF {formatSwissCurrency(paymentsSum)}</span>
          </div>

          {payments.length === 0 && !showPaymentForm && (
            <div className="control-detail-empty">Keine Zahlungen erfasst.</div>
          )}

          {payments.length > 0 && (
            <table className="control-detail-list">
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>ZA Nr.</th>
                  {groups.length > 0 && <th>Vertrag</th>}
                  <th>Datum</th>
                  <th>Art</th>
                  <th className="num">Betrag</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.bezeichnung}</td>
                    <td title={p.rechnungNr ? `Rechn. Nr. ${p.rechnungNr}` : ""}>{p.zaNr || "—"}</td>
                    {groups.length > 0 && <td>{renderPaymentVertrag(p)}</td>}
                    <td>{p.datum ? new Date(p.datum).toLocaleDateString("de-CH") : "—"}</td>
                    <td>{p.istAkonto ? "Akonto" : "Schlusszahlung"}</td>
                    <td className="num">CHF {formatSwissCurrency(p.betrag)}</td>
                    <td className="control-detail-actions">
                      {p.zaDaten && (
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => downloadPdf(p)}
                          title="Zahlungsanweisung als PDF erneut herunterladen"
                        >
                          PDF
                        </button>
                      )}
                      {!readOnly && (
                        <>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() =>
                            p.zaDaten && onZahlungsanweisung ? onZahlungsanweisung(code, p) : startEditPayment(p)
                          }
                          disabled={busy}
                          title={
                            p.zaDaten && onZahlungsanweisung
                              ? "Zahlungsanweisung bearbeiten (Angaben und PDF)"
                              : "Zahlung bearbeiten"
                          }
                        >
                          Bearbeiten
                        </button>
                        <button type="button" className="link-btn danger" onClick={() => removePayment(p)} disabled={busy}>
                          Löschen
                        </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!readOnly && showPaymentForm && (
            <div className="control-detail-form">
              <input
                ref={paymentInputRef}
                type="text"
                placeholder="Bezeichnung *"
                value={paymentForm.bezeichnung}
                onChange={(e) => setPaymentForm({ ...paymentForm, bezeichnung: e.target.value })}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Betrag (CHF, inkl. MwSt)"
                value={paymentForm.betrag}
                onChange={(e) => setPaymentForm({ ...paymentForm, betrag: e.target.value })}
              />
              <input
                type="date"
                value={paymentForm.datum}
                onChange={(e) => setPaymentForm({ ...paymentForm, datum: e.target.value })}
              />
              <select
                value={paymentForm.istAkonto}
                onChange={(e) => setPaymentForm({ ...paymentForm, istAkonto: e.target.value })}
              >
                <option value="">Akonto? *</option>
                <option value="ja">Akonto</option>
                <option value="nein">Schlusszahlung</option>
              </select>
              {groups.length > 0 && (
                <select
                  className="control-detail-vertrag"
                  value={paymentForm.vertragId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, vertragId: e.target.value })}
                  title="Vertrag, zu dem die Zahlung gehört"
                >
                  <option value="">{needsVertragChoice ? "Vertrag… *" : "Vertrag…"}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {groupLabel(g, { withSum: false })}
                    </option>
                  ))}
                </select>
              )}
              <div className="control-detail-form-actions">
                <button type="button" className="btn btn-secondary btn-small" onClick={cancelPayment} disabled={busy}>
                  Abbrechen
                </button>
                <button type="button" className="btn btn-primary btn-small" onClick={submitPayment} disabled={busy}>
                  {editingPaymentId ? "Speichern" : "Hinzufügen"}
                </button>
              </div>
            </div>
          )}

          {!readOnly && !showPaymentForm && (
            <div className="control-detail-add-row">
              <button
                type="button"
                className="control-detail-add"
                onClick={() => {
                  setEditingPaymentId(null);
                  setPaymentForm(emptyPaymentForm);
                  setShowPaymentForm(true);
                }}
              >
                + Zahlung
              </button>
              {onZahlungsanweisung && (
                <button
                  type="button"
                  className="control-detail-add"
                  onClick={() => onZahlungsanweisung(code)}
                  title="Zahlungsanweisung als PDF erzeugen und die Zahlung eintragen"
                >
                  + Zahlungsanweisung
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostControlDetailPanel;
